import React, { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import format from "date-fns/format";
import { useLocation } from "react-router-dom";

import { getOffsetDate } from "fe-shared/src/helpers/dates";
import { TireMaterialConfig } from "fe-shared/src/types/domain";

import { DocumentsInfo } from "..";
import { TRANSACTION_EDIT_BUTTON_PORTAL_ID } from "../../consts";
import { isTransaction } from "../../helpers/transactions";
import {
  Transaction,
  TransactionStatus,
  TransactionType,
  Transfer,
  Retreader,
} from "../../types";
import NonEligibleBalanceBanners from "./components/NonEligibleBalanceBanners";
import RegulatoryYearOverriddenBanner from "./components/RegulatoryYearOverriddenBanner";
import IsWebCreatedBanner from "./components/IsWebCreatedBanner";
import TransactionDataInfo from "./TransactionDataInfo";
import DispositionDataInfo from "./DispositionDataInfo";
import ReTreaderDataInfo from "./ReTreaderDataInfo";

import styles from "./DetailsDataSection.module.scss";

export interface Props {
  transactionOrTransfer: Transaction | Transfer;
  sectionAction?: React.ReactElement;
  children?: React.ReactNode;
  afterDataContent?: React.ReactNode;
  isStaffView?: boolean;
  displayBufferDetails?: boolean;
  parentTransactionType?: TransactionType;
  materials?: TireMaterialConfig[];
}

const DetailsDataSection = ({
  transactionOrTransfer,
  sectionAction,
  children,
  afterDataContent: beforeNonEligibleContent,
  parentTransactionType,
  materials,
  displayBufferDetails,
}: Props) => {
  const { t } = useTranslation();
  const { attachments, scaleTickets, statusCode, regulatoryYear } = transactionOrTransfer;
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const isApproved = statusCode === TransactionStatus.Approved;

  const isRegYearOverridden = regulatoryYear != null;

  const isWebCreated = isTransaction(transactionOrTransfer) && transactionOrTransfer.isCreatedOnWeb;
  
  const isDisposition =
    isTransaction(transactionOrTransfer) &&
    transactionOrTransfer.transactionType === TransactionType.Disposition;
  const isRetreading =
    isTransaction(transactionOrTransfer) &&
    transactionOrTransfer.transactionType === TransactionType.Retreading;
  const isDelivery =
    isTransaction(transactionOrTransfer) &&
    transactionOrTransfer.transactionType === TransactionType.Delivery;
  const isReceipt =
    isTransaction(transactionOrTransfer) &&
    transactionOrTransfer.transactionType === TransactionType.Receipt;
  const isAdjustment =
    isTransaction(transactionOrTransfer) &&
    transactionOrTransfer.transactionType === TransactionType.Adjustment;
  const isDeliveryOrReceipt =
    isTransaction(transactionOrTransfer) &&
    (isReceipt || isDelivery);
  const invoice =
    isDisposition && (transactionOrTransfer as Transaction).dispositionInvoice?.date
      ? (transactionOrTransfer as Transaction).dispositionInvoice
      : null;
  const showNonEligibleBuffer =
    displayBufferDetails && (isDelivery || isAdjustment || isReceipt);
  const showMaterialsByRegYear = useMemo(() => {
    if (!isTransaction(transactionOrTransfer)) return false;
    if (
      transactionOrTransfer.traversedMaterials === null ||
      transactionOrTransfer.traversedMaterials?.length === 0
    )
      return false;

    const { statusCode } = transactionOrTransfer;
    const isInvalid = statusCode === TransactionStatus.Invalid;
    const isApproved = statusCode === TransactionStatus.Approved;
    const isInvoiced = statusCode === TransactionStatus.Invoiced;
    const isAllowedType = [
      TransactionType.Disposition,
      TransactionType.Delivery,
      TransactionType.Receipt,
      TransactionType.Pickup,
      TransactionType.Shipment,
    ].includes(transactionOrTransfer.transactionType);

    return (
      (isAllowedType && !isInvalid) ||
      (isAdjustment && (isApproved || isInvoiced))
    );
  }, [isAdjustment, transactionOrTransfer]);

  const renderDateAndNumber = (
    transalationKey: string,
    date: string,
    number: string
  ) => (
    <Fragment key={number}>
      <div className={styles.dateAndNumber}>
        <span>
          {t(`transactions.details.disposition.${transalationKey}Date`)}
        </span>
        <span>{t(format(getOffsetDate(new Date(date))!, "MMM dd, yyyy"))}</span>
      </div>
      <div className={styles.dateAndNumber}>
        <span>
          {t(`transactions.details.disposition.${transalationKey}Number`)}
        </span>
        <span>{number}</span>
      </div>
    </Fragment>
  );

  return (
    <>
      <div className={styles.transactionDataWrapper}>
        <div className={styles.sectionActionContainer}>
          <div className={styles.transactionDataContainer}>
            {(() => {
              switch (true) {
                case isDisposition:
                  return (
                    <DispositionDataInfo
                      transaction={transactionOrTransfer as Transaction}
                      showComparisonMaterials={searchParams.has("showCompare")}
                    />
                  );
                case isRetreading:
                  return (
                    <ReTreaderDataInfo
                      transaction={
                        (transactionOrTransfer as unknown) as Retreader
                      }
                      materials={materials!}
                    />
                  );
                default:
                  return (
                    <TransactionDataInfo
                      transactionOrTransfer={transactionOrTransfer}
                      parentTransactionType={parentTransactionType}
                      showMaterialsByRegYear={showMaterialsByRegYear}
                      showComparisonMaterials={searchParams.has("showCompare")}
                    />
                  );
              }
            })()}
          </div>
          <div className={styles.actionsButtonsContainer}>
            <div id={TRANSACTION_EDIT_BUTTON_PORTAL_ID} />
            {sectionAction}
          </div>
        </div>

        {beforeNonEligibleContent}
        {isWebCreated && (
          <IsWebCreatedBanner />
        )}
        {showNonEligibleBuffer && (
          <NonEligibleBalanceBanners
            transaction={transactionOrTransfer as Transaction}
          />
        )}
        {children}
      </div>
      {!!attachments?.length && (
        <DocumentsInfo
          attachments={attachments}
          title={t("transactions.details.attachments.title")}
        />
      )}
      {isDisposition && (
        <section>
          {scaleTickets?.map((st) =>
            renderDateAndNumber("scaleTicket", st.createdDate, st.ticketNumber)
          )}
          {!!invoice &&
            renderDateAndNumber(
              "dispositionInvoice",
              invoice.date,
              invoice.number
            )}
        </section>
      )}
      {isRegYearOverridden &&
        isDeliveryOrReceipt && (
          <RegulatoryYearOverriddenBanner
            transaction={transactionOrTransfer as Transaction}
          />
        )}
    </>
  );
};

export default DetailsDataSection;
